<script setup lang="ts">
const { product } = useProduct();
const { reviews } = useProductReviews();
const { groupedProperties, measurements } = useProductDetails(product);

const { apiClient } = useShopwareContext();
const { isLoggedIn } = useUser();
const reviewAdded = ref(false);

const description = computed(() => product.value?.translated?.description);

const productVideos = computed(() => {
  const youtubeIds =
    product.value?.translated?.customFields?.zenit_stratus_youtube_ids?.split(
      ","
    );
  const vimeoIds =
    product.value?.translated?.customFields?.zenit_stratus_vimeo_ids?.split(
      ","
    );

  if (!youtubeIds && !vimeoIds) {
    return [];
  }

  return [
    ...(youtubeIds ?? []).map((youtubeId: string) => {
      const [, title, id = youtubeId] = youtubeId.match(/\[(.*?)\](.*)/) || [];

      return {
        id,
        title: title || undefined,
        url: `https://www.youtube.com/embed/${id}`,
      };
    }),
    ...(vimeoIds ?? []).map((vimeoId: string) => {
      const [, id, title = vimeoId] = vimeoId.match(/\[(.*?)\](.*)/) || [];
      return {
        id,
        title: title || undefined,
        url: `https://player.vimeo.com/video/${id}`,
      };
    }),
  ];
});

const customTab1Title = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab1_title
);

const customTab1Content = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab1_text
);

const customTab2Title = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab2_title
);

const customTab2Content = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab2_text
);

const currentTab = ref("description");

function selectTab(tabId: string) {
  currentTab.value = tabId;
}

const fetchReviews = async () => {
  const reviewsResponse = await apiClient.invoke(
    "readProductReviews post /product/{productId}/reviews",
    {
      pathParams: { productId: product.value.id },
    }
  );
  reviews.value = reviewsResponse.data.elements || [];
};
const handleReviewAdded = () => {
  reviewAdded.value = true;
  fetchReviews();
};
</script>
<template>
  <div class="max-w-2xl w-full mx-auto pt-10 md:pt-14 hidden md:block">
    <div
      class="flex border-b border-cust_green text-base xl:text-xl font-light font-redit_sans overflow-auto hide-scrollbar"
    >
      <!-- Tabs -->
      <button
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === 'description' }"
        @click="selectTab('description')"
      >
        {{ $t("product.description") }}
      </button>
      <button
        v-if="
          Object.keys(groupedProperties).length > 0 || measurements.length > 0
        "
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === 'details' }"
        @click="selectTab('details')"
      >
        {{ $t("product.details") }}
      </button>
      <button
        v-if="productVideos.length > 0"
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === 'video' }"
        @click="selectTab('video')"
      >
        {{ $t("product.video") }}
      </button>
      <button
        v-if="customTab1Title"
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === customTab1Title }"
        @click="selectTab(customTab1Title)"
      >
        {{ customTab1Title }}
      </button>
      <button
        v-if="customTab2Title"
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === customTab2Title }"
        @click="selectTab(customTab2Title)"
      >
        {{ customTab2Title }}
      </button>
      <button
        class="tab-button px-2 xl:px-4 py-2 focus:outline-none"
        :class="{ 'bg-cust_cream': currentTab === 'reviews' }"
        @click="selectTab('reviews')"
      >
        {{ $t("product.reviews.label") }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content mt-4 text-base xl:text-xl">
      <div
        v-if="currentTab === 'description'"
        class="tab-pane px-3 [&_b]:font-[600] listing"
      >
        <div v-html="description"></div>
      </div>
      <div v-else-if="currentTab === 'details'" class="tab-pane px-3">
        <table class="w-full mt-2">
          <tbody>
            <tr
              v-for="group in groupedProperties"
              :key="group.group.id"
              class="flex items-center justify-between lg:justify-normal pb-5 mb-5 border-b border-cust_green border-opacity-25"
            >
              <th
                class="font-medium lg:min-w-[35%] lg:w-[35%] mr-5 p-0 text-left"
              >
                <span class="align-middle">
                  {{ group.group?.translated?.name }}
                </span>
              </th>
              <td class="text-right lg:text-left">
                <template
                  v-for="(property, index) in group.properties"
                  :key="property.id"
                >
                  {{ property?.translated?.name
                  }}<span v-if="index !== group.properties?.length - 1"
                    >,
                  </span>
                </template>
              </td>
            </tr>
            <tr
              v-for="measurement in measurements"
              :key="measurement.label"
              class="flex items-center justify-between lg:justify-normal pb-5 mb-5 border-b border-cust_green border-opacity-25"
            >
              <th
                class="font-medium lg:min-w-[35%] lg:w-[35%] mr-5 p-0 text-left"
              >
                <span class="align-middle">
                  {{ measurement.label }}
                </span>
              </th>
              <td class="text-right lg:text-left">
                {{ measurement.value }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="currentTab === 'video'" class="tab-pane px-3 listing">
        <div class="flex gap-4 justify-between flex-wrap">
          <template v-for="video in productVideos">
            <div class="w-full">
              <p class="font-medium mb-3" v-if="video.title">
                {{ video.title }}
              </p>
              <div class="relative pb-[56.25%] h-0">
                <iframe
                  :src="video.url"
                  class="absolute top-0 left-0 w-full h-full"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
      <div
        v-else-if="currentTab === customTab1Title"
        class="tab-pane px-3 [&_b]:font-[600] listing"
      >
        <p v-html="customTab1Content"></p>
      </div>
      <div
        v-else-if="currentTab === customTab2Title"
        class="tab-pane px-3 [&_b]:font-[600] listing"
      >
        <p v-html="customTab2Content"></p>
      </div>
      <div
        v-else-if="currentTab === 'reviews'"
        class="tab-pane px-3 [&_b]:font-[600] listing"
      >
        <ProductReviews :product="product" :reviews="reviews" />
        <ProductReviewsForm
          v-if="isLoggedIn && !reviewAdded"
          :product-id="product.id"
          @success="handleReviewAdded"
        />
        <div v-if="reviewAdded">{{ $t("product.messages.reviewAdded") }}</div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
